import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError, streamPost, t } from "@/lib/api";
import { AiText, ModelPicker, PageHeader } from "@/components/Common";

const FEAS = () => [["cout", t("Coût maîtrisé", "Controlled cost")], ["temps", t("Délai réaliste", "Realistic timeline")], ["competences", t("Compétences disponibles", "Available skills")], ["alignement", t("Alignement stratégique", "Strategic alignment")], ["adhesion", t("Adhésion des équipes", "Team buy-in")]];
const scoreColor = (s) => (s >= 70 ? "text-emerald-600" : s >= 45 ? "text-amber-600" : "text-rose-600");
const verdictOf = (s) => (s >= 70 ? t("Solide — prête à tester", "Solid — ready to test") : s >= 45 ? t("Prometteuse — à consolider", "Promising — needs strengthening") : t("Fragile — à retravailler", "Fragile — needs rework"));

const Section = ({ n, title: t, children }) => (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
      <div className="flex items-center gap-3"><span className="font-mono text-xs text-indigo-600">0{n}</span><h2 className="font-display text-xl font-semibold">{t}</h2></div>
      <div className="mt-5">{children}</div>
    </section>
  );

function SavedList({ items, onDelete }) {
  if (!items.length) return null;
  return (
    <div className="mt-14">
      <h2 className="font-display text-xl font-semibold">{t("Mes matrices", "My matrices")}</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="matrix-saved-list">
        {items.map((m) => (
          <div key={m.matrix_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`matrix-item-${m.matrix_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div><div className="font-semibold">{m.title}</div><div className="text-xs text-slate-500 mt-1">{verdictOf(m.solidity_score)}</div></div>
              <div className={`font-display text-3xl font-bold ${scoreColor(m.solidity_score)}`}>{m.solidity_score}</div>
            </div>
            <div className="mt-3 flex gap-4 font-mono text-xs text-slate-500"><span>{t("Faisab.", "Feas.")} {m.feasibility_score}</span><span>{t("Risque", "Risk")} {m.risk_score}</span><span>{t("Biais", "Biases")} {m.bias_check_score}</span>
              <button onClick={() => onDelete(m.matrix_id)} data-testid={`matrix-delete-${m.matrix_id}`} className="ml-auto text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Matrix() {
  const [params] = useSearchParams();
  const [biases, setBiases] = useState([]);
  const [title, setTitle] = useState(params.get("title") || "");
  const [description, setDescription] = useState("");
  const [risks, setRisks] = useState([{ label: "", probability: 3, impact: 3 }]);
  const [checked, setChecked] = useState({});
  const [feas, setFeas] = useState(Object.fromEntries(FEAS().map(([k]) => [k, 3])));
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const [model, setModel] = useState("claude");
  const [ai, setAi] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const load = () => api.get("/matrix").then(({ data }) => setSaved(data));
  useEffect(() => { api.get("/matrix/biases").then(({ data }) => setBiases(data)); load(); }, []);

  const setRisk = (i, k, v) => setRisks(risks.map((r, n) => (n === i ? { ...r, [k]: v } : r)));
  const submit = async () => {
    try {
      const { data } = await api.post("/matrix", { title, description, idea_id: params.get("idea"), risks: risks.filter((r) => r.label.trim()), biases: checked, feasibility: feas });
      setResult(data); toast.success(t("Matrice enregistrée ! +20 pts", "Matrix saved! +20 pts")); load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const challenge = async () => {
    setAiBusy(true); setAi("");
    const ctx = `${t("Idée", "Idea")} : ${title}\n${description}\n${t("Risques", "Risks")} : ${risks.map((r) => `${r.label} (P${r.probability}/I${r.impact})`).join(", ")}\n${t("Biais vérifiés", "Biases checked")} : ${biases.filter((b) => checked[b.id]).map((b) => b.label).join(", ") || t("aucun", "none")}\n${t("Faisabilité", "Feasibility")} : ${FEAS().map(([k, l]) => `${l} ${feas[k]}/5`).join(", ")}`;
    try { await streamPost("/ai/critique", { context: ctx, model }, setAi); } catch (e) { toast.error(e.message); }
    setAiBusy(false);
  };
  const del = async (id) => { await api.delete(`/matrix/${id}`); load(); };


  return (
    <div>
      <PageHeader eyebrow={t("Esprit critique", "Critical thinking")} title={t("Matrice de décision", "Decision matrix")} subtitle={t("Testez la solidité d'une idée avant de la lancer : risques, biais cognitifs et faisabilité.", "Stress-test an idea before launching it: risks, cognitive biases and feasibility.")} />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <section className="space-y-3">
            <Input data-testid="matrix-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("L'idée ou la décision à tester", "The idea or decision to test")} className="h-12 bg-white text-base" />
            <Textarea data-testid="matrix-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder={t("Contexte (optionnel)", "Context (optional)")} className="bg-white" />
          </section>
          <Section n={1} title={t("Analyse des risques", "Risk analysis")}>
            <div className="space-y-4">
              {risks.map((r, i) => (
                <div key={i} className="grid gap-3 sm:grid-cols-12 items-center" data-testid={`matrix-risk-row-${i}`}>
                  <Input data-testid={`matrix-risk-label-${i}`} value={r.label} onChange={(e) => setRisk(i, "label", e.target.value)} placeholder={t("Ex. : rejet par les équipes terrain", "E.g. rejection by field teams")} className="sm:col-span-6" />
                  {["probability", "impact"].map((k) => (
                    <label key={k} className="sm:col-span-2 text-xs text-slate-500">{k === "probability" ? t("Probabilité", "Probability") : "Impact"}
                      <select data-testid={`matrix-risk-${k}-${i}`} value={r[k]} onChange={(e) => setRisk(i, k, Number(e.target.value))} className="mt-1 block w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm">
                        {[1, 2, 3, 4, 5].map((v) => <option key={v}>{v}</option>)}
                      </select>
                    </label>
                  ))}
                  <div className="sm:col-span-2 flex items-center gap-2">
                    <span className={`rounded-md px-2 py-1 font-mono text-xs ${r.probability * r.impact >= 15 ? "bg-rose-100 text-rose-700" : r.probability * r.impact >= 8 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.probability * r.impact}</span>
                    {risks.length > 1 && <button onClick={() => setRisks(risks.filter((_, n) => n !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
              ))}
              <button data-testid="matrix-add-risk" onClick={() => setRisks([...risks, { label: "", probability: 3, impact: 3 }])} className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600"><Plus className="h-4 w-4" /> {t("Ajouter un risque", "Add a risk")}</button>
            </div>
          </Section>
          <Section n={2} title={t("Biais cognitifs à vérifier", "Cognitive biases to check")}>
            <div className="grid gap-3 sm:grid-cols-2">
              {biases.map((b) => (
                <label key={b.id} className={`flex gap-3 rounded-2xl border p-4 cursor-pointer transition-colors ${checked[b.id] ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <Checkbox data-testid={`matrix-bias-${b.id}`} checked={!!checked[b.id]} onCheckedChange={(v) => setChecked({ ...checked, [b.id]: !!v })} className="mt-0.5" />
                  <span><span className="block text-sm font-semibold">{b.label}</span><span className="block text-xs text-slate-500 mt-0.5">{b.question}</span></span>
                </label>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-400">{t("Cochez un biais uniquement si vous avez vérifié qu'il n'influence pas votre jugement.", "Only tick a bias once you've checked it isn't influencing your judgment.")}</p>
          </Section>
          <Section n={3} title={t("Faisabilité", "Feasibility")}>
            <div className="space-y-5">
              {FEAS().map(([k, label]) => (
                <div key={k}>
                  <div className="flex justify-between text-sm"><span>{label}</span><span className="font-mono text-indigo-600">{feas[k]}/5</span></div>
                  <Slider data-testid={`matrix-feasibility-${k}`} min={1} max={5} step={1} value={[feas[k]]} onValueChange={([v]) => setFeas({ ...feas, [k]: v })} className="mt-2" />
                </div>
              ))}
            </div>
          </Section>
        </div>
        <aside className="space-y-6 lg:sticky lg:top-8 self-start">
          <div className="rounded-3xl bg-slate-900 p-6 text-white" data-testid="matrix-result-panel">
            <div className="text-xs font-semibold uppercase tracking-wider text-amber-400">{t("Score de solidité", "Robustness score")}</div>
            {result ? (
              <>
                <div className="mt-3 font-display text-6xl font-bold" data-testid="matrix-solidity-score">{result.solidity_score}</div>
                <div className="mt-1 text-sm text-slate-300" data-testid="matrix-verdict">{verdictOf(result.solidity_score)}</div>
                <div className="mt-6 space-y-3 text-sm">
                  {[[t("Faisabilité", "Feasibility"), result.feasibility_score, "bg-emerald-400"], [t("Risque max", "Max risk"), result.risk_score, "bg-rose-400"], [t("Biais vérifiés", "Biases checked"), result.bias_check_score, "bg-indigo-400"]].map(([l, v, c]) => (
                    <div key={l}><div className="flex justify-between text-slate-300"><span>{l}</span><span className="font-mono">{v}</span></div><div className="mt-1 h-1.5 rounded-full bg-slate-700"><div className={`h-full rounded-full ${c}`} style={{ width: `${v}%` }} /></div></div>
                  ))}
                </div>
              </>
            ) : <p className="mt-3 text-sm text-slate-400">{t("Remplissez les trois trames puis calculez le score.", "Fill in the three frameworks, then calculate the score.")}</p>}
            <button data-testid="decision-matrix-submit" disabled={title.trim().length < 3} onClick={submit} className="mt-6 w-full rounded-full bg-amber-400 py-3 font-semibold text-slate-900 hover:bg-amber-300 disabled:opacity-40 transition-colors">{t("Calculer & enregistrer", "Calculate & save")}</button>
          </div>
          <div className="rounded-3xl border border-rose-200 bg-rose-50/60 p-6">
            <div className="font-display text-lg font-semibold">{t("Contradicteur IA", "AI challenger")}</div>
            <div className="mt-3"><ModelPicker value={model} onChange={setModel} /></div>
            <button data-testid="matrix-ai-challenge-button" disabled={aiBusy || title.trim().length < 3} onClick={challenge} className="mt-4 inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:bg-rose-700 transition-colors">
              <Sparkles className="h-4 w-4" /> {aiBusy ? t("Réflexion…", "Thinking…") : t("Challenger mon analyse", "Challenge my analysis")}
            </button>
            {ai && <div className="mt-4 rounded-2xl bg-white p-4"><AiText text={ai} testId="matrix-ai-output" /></div>}
          </div>
        </aside>
      </div>
      <SavedList items={saved} onDelete={del} />
    </div>
  );
}
